import React, { useEffect, useState } from 'react';
import { Card, Table, Button, Tag, Modal, Form, Select, Input, message, Space, Statistic, Row, Col, Descriptions, Image } from 'antd';
import { EyeOutlined, EditOutlined, ReloadOutlined, LinkOutlined } from '@ant-design/icons';
import moment from 'moment';

import { fetchTourEnquiries, fetchNewTourEnquiriesCount, updateTourEnquiryStatus } from '../../../../redux/api/inquiries.api';

import './styles.scss';

const { Option } = Select;
const { TextArea } = Input;

const Inquiries = () => {
    const [enquiries, setEnquiries] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });
    const [filters, setFilters] = useState({});
    const [newEnquiriesCount, setNewEnquiriesCount] = useState(0);
    const [statusModalVisible, setStatusModalVisible] = useState(false);
    const [viewModalVisible, setViewModalVisible] = useState(false);
    const [selectedEnquiry, setSelectedEnquiry] = useState(null);
    const [form] = Form.useForm();

    useEffect(() => {
        fetchEnquiries();
        fetchNewCount();
    }, []);

    const fetchEnquiries = async (page = 1, filterParams = {}) => {
        setLoading(true);
        try {
            const response = await fetchTourEnquiries(page, filterParams);
            if (response.status === 200) {
                const data = response.data;
                setEnquiries(data);
                setPagination(prev => ({
                    ...prev,
                    current: page,
                    total: data[0]?.totalEnquiries || 0,
                }));
            }
        } catch (error) {
            message.error('Failed to fetch enquiries');
        }
        setLoading(false);
    };

    const fetchNewCount = async () => {
        try {
            const response = await fetchNewTourEnquiriesCount();
            if (response.status === 200) {
                const data = response.data;
                setNewEnquiriesCount(data.newEnquiriesCount);
            }
        } catch (error) {
            console.error('Failed to fetch new enquiries count');
        }
    };

    const handleTableChange = (pagination, tableFilters) => {
        const newFilters = {
            ...filters,
            status: tableFilters.status?.[0] || undefined,
            userType: tableFilters.userType?.[0] || undefined,
        };
        setFilters(newFilters);
        fetchEnquiries(pagination.current, newFilters);
    };

    const handleStatusUpdate = (enquiry) => {
        setSelectedEnquiry(enquiry);
        form.setFieldsValue({
            status: enquiry.status,
            notes: enquiry.notes || '',
        });
        setStatusModalVisible(true);
    };

    const handleViewDetails = (enquiry) => {
        setSelectedEnquiry(enquiry);
        setViewModalVisible(true);
    };

    const handleStatusSubmit = async (values) => {
        try {
            setLoading(true);
            const response = await updateTourEnquiryStatus(
                selectedEnquiry.id,
                values.status,
                values.notes
            );

            if (response.status === 200) {
                message.success('Enquiry status updated successfully');
                setStatusModalVisible(false);
                fetchEnquiries(pagination.current, filters);
                fetchNewCount();
            } else {
                message.error('Failed to update enquiry status');
            }
        } catch (error) {
            message.error('Failed to update enquiry status');
        }
        setLoading(false);
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'NEW': return 'blue';
            case 'PROCESSING': return 'orange';
            case 'COMPLETED': return 'green';
            case 'CANCELLED': return 'red';
            default: return 'default';
        }
    };

    const columns = [
        {
            title: 'Date',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 120,
            sorter: true,
        },
        {
            title: 'Contact Info',
            key: 'contact',
            width: 200,
            render: (record) => (
                <div>
                    <div><strong>{record.userName || record.registeredUserName}</strong></div>
                    <div>{record.email || record.registeredUserEmail}</div>
                    <div>{record.contactNo || record.registeredUserContact}</div>
                </div>
            ),
        },
        {
            title: 'Tour Package',
            dataIndex: 'packageName',
            key: 'packageName',
            width: 200,
            render: (text, record) => (
                <div>
                    <div><strong>{text}</strong></div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                        ID: {record.tourPackageId}
                    </div>
                </div>
            ),
        },
        {
            title: 'Subject',
            dataIndex: 'subject',
            key: 'subject',
            width: 150,
            ellipsis: true,
        },
        {
            title: 'Message',
            dataIndex: 'message',
            key: 'message',
            width: 200,
            ellipsis: true,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 120,
            filters: [
                { text: 'New', value: 'NEW' },
                { text: 'Processing', value: 'PROCESSING' },
                { text: 'Completed', value: 'COMPLETED' },
                { text: 'Cancelled', value: 'CANCELLED' },
            ],
            render: (status) => (
                <Tag color={getStatusColor(status)}>
                    {status}
                </Tag>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 160,
            fixed: 'right',
            render: (_, record) => (
                <Space>
                    <Button
                        type="default"
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => handleViewDetails(record)}
                    >
                        View
                    </Button>
                    <Button
                        type="primary"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => handleStatusUpdate(record)}
                    >
                        Update
                    </Button>
                </Space>
            ),
        },
    ];

    return (
        <div className="inquiries-page">
            <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="New Enquiries"
                            value={newEnquiriesCount}
                            valueStyle={{ color: '#1890ff' }}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="Total Enquiries"
                            value={pagination.total}
                            valueStyle={{ color: '#52c41a' }}
                        />
                    </Card>
                </Col>
                <Col span={12}>
                    <div style={{ textAlign: 'right', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        <Button
                            type="primary"
                            icon={<ReloadOutlined />}
                            onClick={() => {
                                fetchEnquiries(1, {});
                                fetchNewCount();
                                setFilters({});
                            }}
                        >
                            Refresh
                        </Button>
                    </div>
                </Col>
            </Row>

            <Card title="Tour Enquiries" className="enquiries-table-card">
                <Table
                    columns={columns}
                    dataSource={enquiries}
                    rowKey="id"
                    loading={loading}
                    pagination={{
                        ...pagination,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) =>
                            `${range[0]}-${range[1]} of ${total} enquiries`,
                    }}
                    onChange={handleTableChange}
                    scroll={{ x: 1200 }}
                />
            </Card>

            <Modal
                title="Update Enquiry Status"
                open={statusModalVisible}
                onCancel={() => setStatusModalVisible(false)}
                footer={null}
                width={500}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleStatusSubmit}
                >
                    <Form.Item
                        label="Status"
                        name="status"
                        rules={[{ required: true, message: 'Please select a status' }]}
                    >
                        <Select placeholder="Select status">
                            <Option value="NEW">New</Option>
                            <Option value="PROCESSING">Processing</Option>
                            <Option value="COMPLETED">Completed</Option>
                            <Option value="CANCELLED">Cancelled</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label="Notes"
                        name="notes"
                        rules={[{ required: true, message: 'Please add any notes about this status update' }]}
                    >
                        <TextArea
                            rows={4}
                            placeholder="Add any notes about this status update..."
                        />
                    </Form.Item>

                    <Form.Item>
                        <Space>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={loading}
                            >
                                Update Status
                            </Button>
                            <Button onClick={() => setStatusModalVisible(false)}>
                                Cancel
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title="Enquiry Details"
                open={viewModalVisible}
                onCancel={() => setViewModalVisible(false)}
                footer={[
                    <Button key="close" onClick={() => setViewModalVisible(false)}>
                        Close
                    </Button>
                ]}
                width={800}
            >
                {selectedEnquiry && (
                    <div className="enquiry-details">
                        <Descriptions bordered column={2} size="small">
                            <Descriptions.Item label="Enquiry ID" span={2}>
                                {selectedEnquiry.id}
                            </Descriptions.Item>

                            <Descriptions.Item label="Status">
                                <Tag color={getStatusColor(selectedEnquiry.status)}>
                                    {selectedEnquiry.status}
                                </Tag>
                            </Descriptions.Item>

                            <Descriptions.Item label="User Type">
                                <Tag color={selectedEnquiry.userType === 'registered' ? 'green' : 'blue'}>
                                    {selectedEnquiry.userType?.toUpperCase() || 'GUEST'}
                                </Tag>
                            </Descriptions.Item>

                            <Descriptions.Item label="Contact Name">
                                {selectedEnquiry.userName || selectedEnquiry.registeredUserName}
                            </Descriptions.Item>

                            <Descriptions.Item label="Email">
                                {selectedEnquiry.email || selectedEnquiry.registeredUserEmail}
                            </Descriptions.Item>

                            <Descriptions.Item label="Contact Number">
                                {selectedEnquiry.contactNo || selectedEnquiry.registeredUserContact}
                            </Descriptions.Item>

                            <Descriptions.Item label="Country">
                                {selectedEnquiry.country || 'Not specified'}
                            </Descriptions.Item>

                            <Descriptions.Item label="Subject">
                                {selectedEnquiry.subject}
                            </Descriptions.Item>

                            <Descriptions.Item label="Origin">
                                {selectedEnquiry.origin}
                            </Descriptions.Item>

                            <Descriptions.Item label="Message" span={2}>
                                <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                    {selectedEnquiry.message}
                                </div>
                            </Descriptions.Item>

                            {selectedEnquiry.notes && (
                                <Descriptions.Item label="Notes" span={2}>
                                    <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                        {selectedEnquiry.notes}
                                    </div>
                                </Descriptions.Item>
                            )}

                            <Descriptions.Item label="Created At">
                                {selectedEnquiry.createdAt}
                            </Descriptions.Item>

                            <Descriptions.Item label="Updated At">
                                {selectedEnquiry.updatedAt}
                            </Descriptions.Item>
                        </Descriptions>

                        {selectedEnquiry.packageName && (
                            <div style={{ marginTop: 24 }}>
                                <h4>Tour Package Details</h4>
                                <Descriptions bordered column={1} size="small">
                                    <Descriptions.Item label="Package Name">
                                        <Space>
                                            {selectedEnquiry.packageName}
                                            <Button
                                                type="link"
                                                size="small"
                                                icon={<LinkOutlined />}
                                                onClick={() => window.open(`/dashboard/tours/tour-packages/${selectedEnquiry.tourPackageId}`, '_blank')}
                                                style={{ padding: 0, height: 'auto' }}
                                            >
                                                View Package
                                            </Button>
                                        </Space>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Package ID">
                                        {selectedEnquiry.tourPackageId}
                                    </Descriptions.Item>
                                </Descriptions>

                                {selectedEnquiry.packageImages && (
                                    <div style={{ marginTop: 16 }}>
                                        <h5>Package Images</h5>
                                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                            {selectedEnquiry.packageImages.split(',').slice(0, 4).map((image, index) => (
                                                <Image
                                                    key={index}
                                                    width={100}
                                                    height={80}
                                                    src={image.trim()}
                                                    style={{ objectFit: 'cover', borderRadius: 4 }}
                                                    fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYxN"
                                                />
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default Inquiries;
